<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          <span>{{ pageTitle }}</span>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                ref="patient"
                outlined
                v-model="form.patient_id"
                :items="patientItems"
                label="Patient *"
                prepend-inner-icon="mdi-account-circle"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.patient_id"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                ref="device"
                outlined
                v-model="form.device_id"
                :items="deviceItems"
                label="Device *"
                prepend-inner-icon="mdi-medical-bag"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.device_id"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                ref="procedure"
                outlined
                v-model="form.procedure_id"
                :items="procedureItems"
                label="Procedure"
                prepend-inner-icon="mdi-clipboard-list"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.procedure_id"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                ref="status"
                outlined
                v-model="form.status"
                :items="statusItems"
                label="Status"
                prepend-inner-icon="mdi-check-circle"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.status"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.start_time"
                outlined
                label="Start Time"
                prepend-inner-icon="mdi-clock-start"
                type="time"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.start_time"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.end_time"
                outlined
                label="End Time"
                prepend-inner-icon="mdi-clock-end"
                type="time"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.end_time"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.duration"
                outlined
                label="Duration (minutes)"
                prepend-inner-icon="mdi-timer"
                type="number"
                min="0"
                step="1"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.duration"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.distance_covered"
                outlined
                label="Distance Covered (meters)"
                prepend-inner-icon="mdi-map-marker-distance"
                type="number"
                min="0"
                step="1"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.distance_covered"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.unweighting_percentage"
                outlined
                label="Unweighting Percentage (%)"
                prepend-inner-icon="mdi-weight-lifter"
                type="number"
                min="0"
                max="100"
                step="1"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.unweighting_percentage"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.lifted_weight"
                outlined
                label="Lifted Weight (kg)"
                prepend-inner-icon="mdi-dumbbell"
                type="number"
                min="0"
                step="0.1"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.lifted_weight"
              ></v-text-field>
            </v-col>
            <v-col sm="12">
              <v-text-field
                v-model="form.session_notes"
                outlined
                label="Session Notes (ID)"
                prepend-inner-icon="mdi-note-text"
                type="number"
                min="0"
                step="1"
                hint="Enter a numeric ID reference for session notes"
                :clearable="!isViewMode"
                :readonly="isViewMode"
                :disabled="isViewMode"
                :error-messages="errors.session_notes"
              ></v-text-field>
            </v-col>
            <v-col sm="4">
              <v-switch
                class="mt-0 ml-5"
                v-model="form.is_active"
                inset
                :label="`Is Active: ${form.is_active ? 'YES' : 'NO'}`"
                :readonly="isViewMode"
                :disabled="isViewMode"
              ></v-switch>
            </v-col>
          </v-row>
          
          <!-- Session Summary Card -->
          <v-card v-if="form.id" class="mb-4" elevation="2" rounded="lg">
            <v-card-title class="text-h6">Session Summary</v-card-title>
            <v-card-text>
              <v-row>
                <v-col sm="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Intensity Level</v-list-item-title>
                      <v-list-item-subtitle>{{ getIntensityLevel() }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col sm="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Efficiency Score</v-list-item-title>
                      <v-list-item-subtitle>{{ getEfficiencyScore() || 'N/A' }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col sm="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Status</v-list-item-title>
                      <v-list-item-subtitle>
                        <v-chip
                          small
                          :color="getStatusColor(form.status)"
                          text-color="white"
                        >
                          {{ getStatusText(form.status) }}
                        </v-chip>
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
                <v-col sm="6">
                  <v-list-item>
                    <v-list-item-content>
                      <v-list-item-title>Created At</v-list-item-title>
                      <v-list-item-subtitle>{{ formatDate(form.created_at) }}</v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-btn
            v-if="!isViewMode"
            :loading="isLoading"
            :disabled="isLoading"
            color="primary"
            rounded
            dark
            @click="save"
          >
            Update Therapy Session
          </v-btn>
          
          <!-- View Mode Actions -->
          <div v-if="isViewMode" class="mt-4">
            <v-btn
              color="primary"
              rounded
              dark
              :to="`/therapy_sessions/${$route.params.sessionId}`"
              class="mr-2"
            >
              <v-icon left>mdi-pencil</v-icon>
              Edit Session
            </v-btn>
            <v-btn
              color="success"
              rounded
              dark
              @click="viewSessionLogs"
              class="mr-2"
            >
              <v-icon left>mdi-file-document-multiple</v-icon>
              View Session Logs
            </v-btn>
            <v-btn
              color="info"
              rounded
              dark
              @click="viewControlLogs"
            >
              <v-icon left>mdi-console</v-icon>
              View Control Logs
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </v-main>
</template>
<script>
import axios from "@/axios";
export default {
  name: "TherapySessionEditPage",
  data() {
    return {
      form: {},
      isLoading: false,
      patientItems: [],
      deviceItems: [],
      procedureItems: [],
      statusItems: [
        {
          id: 1,
          text: "Completed",
          value: 1,
        },
        {
          id: 2,
          text: "Stopped",
          value: 2,
        },
        {
          id: 3,
          text: "Emergency Stopped",
          value: 3,
        },
      ],
    };
  },
  computed: {
    isViewMode() {
      return this.$route.query.mode === 'view';
    },
    pageTitle() {
      return this.isViewMode ? 'View Therapy Session' : 'Edit Therapy Session';
    },
    errors() {
      return this.$store.getters['errors/getErrors'];
    },
  },
  async mounted() {
    await this.getMasters();
    await this.getData();
  },
  methods: {
    async getMasters() {
      try {
        // Clear existing arrays
        this.patientItems = [];
        this.deviceItems = [];
        this.procedureItems = [];
        
        const response = await axios.get(`masters?context=therapy_session`);
        const masters = response.data;
        
        console.log("Masters response:", masters);
        
        // Patients
        if (masters.patients && Array.isArray(masters.patients)) {
          masters.patients.forEach((patient) => {
            this.patientItems.push({
              id: patient.id,
              text: `${patient.first_name} ${patient.last_name}`,
              value: patient.id,
            });
          });
        }
        console.log("Patient items:", this.patientItems);
        
        // Devices
        if (masters.devices && Array.isArray(masters.devices)) {
          masters.devices.forEach((device) => {
            this.deviceItems.push({
              id: device.id,
              text: `${device.model_name || device.serial_number} (${device.serial_number})`,
              value: device.id,
            });
          });
        }
        console.log("Device items:", this.deviceItems);
        
        // Procedures
        if (masters.procedures && Array.isArray(masters.procedures)) {
          masters.procedures.forEach((procedure) => {
            this.procedureItems.push({
              id: procedure.id,
              text: procedure.name,
              value: String(procedure.id), // Convert to string to match procedure_id field type
            });
          });
        }
        console.log("Procedure items:", this.procedureItems);
      } catch (error) {
        console.error("Error fetching masters:", error);
        // redirect to Home Page
        if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      }
    },
    async getData() {
      try {
        this.isLoading = true;
        let encryptedId = this.$route.params.sessionId;
        
        const response = await axios.get(`/therapy_sessions/${encryptedId}`);
        this.form = response.data.data;
        this.old_json = response.data.data;
        
        console.log("Form data loaded:", this.form);
        console.log("Patient ID:", this.form.patient_id);
        console.log("Patient object:", this.form.patient);
        console.log("Device object:", this.form.device);
        console.log("Procedure object:", this.form.procedure);
        
        // Add the loaded related objects to dropdown items if they exist and are not already in the list
        this.addRelatedItemsToDropdowns();
        
      } catch (error) {
        console.error("Error fetching session data:", error);
        // redirect to Home Page
        if (error.response && error.response.status === 401) {
          //window.location.href = "/auth/login";
        }
      } finally {
        this.isLoading = false;
      }
    },
    addRelatedItemsToDropdowns() {
      // Add patient if exists and not in list
      if (this.form.patient && this.form.patient_id) {
        const patientExists = this.patientItems.some(item => item.value === this.form.patient_id);
        if (!patientExists) {
          this.patientItems.push({
            id: this.form.patient.id,
            text: `${this.form.patient.first_name} ${this.form.patient.last_name}`,
            value: this.form.patient.id,
          });
          console.log("Added patient to dropdown:", this.form.patient);
        }
      }
      
      // Add device if exists and not in list
      if (this.form.device && this.form.device_id) {
        const deviceExists = this.deviceItems.some(item => item.value === this.form.device_id);
        if (!deviceExists) {
          this.deviceItems.push({
            id: this.form.device.id,
            text: `${this.form.device.model_name || this.form.device.serial_number} (${this.form.device.serial_number})`,
            value: this.form.device.id,
          });
          console.log("Added device to dropdown:", this.form.device);
        }
      }
      
      // Add procedure if exists and not in list
      if (this.form.procedure && this.form.procedure_id) {
        // Convert both values to strings for comparison since procedure_id is stored as string
        const procedureExists = this.procedureItems.some(item => String(item.value) === String(this.form.procedure_id));
        if (!procedureExists) {
          this.procedureItems.push({
            id: this.form.procedure.id,
            text: this.form.procedure.name,
            value: String(this.form.procedure_id), // Use the actual stored procedure_id (string) as value
          });
          console.log("Added procedure to dropdown:", this.form.procedure);
        }
      }
    },
    async save() {
      try {
        this.isLoading = true;
        await axios
          .put(`/therapy_sessions/${this.$route.params.sessionId}`, this.form)
          .then(() => {
            // Session updated successfully
          })
          .catch((error) => {
            console.error("Error updating session:", error);
          });
        this.isLoading = false;
        this.$router.push("/therapy_sessions");
      } catch (e) {
        this.isLoading = false;
      }
    },
    getStatusText(status) {
      switch(status) {
        case 1:
          return 'Completed';
        case 2:
          return 'Stopped';
        case 3:
          return 'Emergency Stopped';
        default:
          return 'Unknown';
      }
    },
    getStatusColor(status) {
      switch(status) {
        case 1:
          return 'success';
        case 2:
          return 'warning';
        case 3:
          return 'error';
        default:
          return 'secondary';
      }
    },
    getIntensityLevel() {
      const distance = this.form.distance_covered || 0;
      const duration = parseFloat(this.form.duration) || 0;
      const unweighting = this.form.unweighting_percentage || 50;

      // Calculate intensity based on speed and load
      const speed = duration > 0 ? distance / duration : 0;
      const load = 100 - unweighting; // Higher unweighting = lower load

      if (speed <= 10 && load <= 30) {
        return 'Low';
      } else if (speed <= 20 && load <= 60) {
        return 'Moderate';
      } else if (speed <= 30 && load <= 80) {
        return 'High';
      } else {
        return 'Very High';
      }
    },
    getEfficiencyScore() {
      if (!this.form.distance_covered || !this.form.duration || this.form.duration == 0) {
        return null;
      }

      return Math.round((this.form.distance_covered / parseFloat(this.form.duration)) * 100) / 100;
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      return new Date(dateString).toLocaleString();
    },
    viewSessionLogs() {
      // Navigate to session logs page with current therapy session filter
      this.$router.push({
        path: '/session_logs',
        query: {
          therapy_session_id: this.$route.params.sessionId,
        }
      });
    },
    viewControlLogs() {
      // Navigate to control logs page with current therapy session filter
      this.$router.push({
        path: '/control_logs',
        query: {
          therapy_session_id: this.$route.params.sessionId,
        }
      });
    },
  },
};
</script>